import React, { useState, useEffect } from 'react';
// components
import LocationsColumns from 'src/organizations/components/tables/LocationsColumns';
import LocationsToolbar from 'src/organizations/components/toolbars/LocationsToolbar';
import Modal from 'shared/modals/Modal';
import MaintenanceTable from 'shared/tables/MaintenanceTable';
import SearchInput from 'shared/inputs/SearchInput';
// fetch
import { getLocations, getLocationTypes } from 'src/fetch/locations';

const LocationPicker = ({
  initialLocations,
  value,
  onChange,
  onClose,
  disabled,
  canClear,
  className,
  restrictedTypes,
  selectLabel = 'Search location...',
  hidePickerButtons = false
}) => {
  // Locations
  const [locations, setLocations] = useState(initialLocations || []);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [text, setText] = useState('');

  // static lists
  const [locationTypes, setLocationTypes] = useState([]);

  // modal
  const [isShowing, setIsShowing] = useState(false);

  // search
  const [search, setSearch] = useState(null);

  // reset
  const [reset, setReset] = useState(false);

  const refresh = async () => {
    setIsLoading(true);
    try {
      if (restrictedTypes && isShowing) {
        // We don't need to get the locations if we are showing and have restrictedTypes, the toolbar will handle it.
        const [locationTypesResponse] = await Promise.all([getLocationTypes()]);
        setLocationTypes(locationTypesResponse);
      } else if (locationTypes.length === 0) {
        const [locationsResponse, locationTypesResponse] = await Promise.all([getLocations(), getLocationTypes()]);
        setLocations(locationsResponse);
        setLocationTypes(locationTypesResponse);
        setIsLoading(false);
      } else {
        const locationsResponse = await getLocations(search || {});
        setLocations(locationsResponse);
        setIsLoading(false);
      }
    } catch (error) {
      setApiError('Problem loading locations. Please try again');
      setIsLoading(false);
    }
  };

  // initial load we need to refresh locations
  useEffect(() => {
    if (!initialLocations || initialLocations.length === 0) {
      refresh();
    }
  }, []);

  // refresh locations anytime we open the modal
  useEffect(() => {
    if (isShowing) {
      refresh();
    }
  }, [isShowing]);

  const shallowLoad = async () => {
    try {
      setIsLoading(true);
      const locationsResponse = await getLocations(search);
      setLocations(locationsResponse);
      setIsLoading(false);
    } catch (error) {
      setApiError('Problem loading locations. Please try again');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (search || search === '') {
      shallowLoad();
      setReset(false);
    }
  }, [search]);

  const getRestrictedTypes = () => {
    if (
      restrictedTypes &&
      Array.isArray(restrictedTypes) &&
      restrictedTypes.every(t => Number.isInteger(t)) &&
      locationTypes &&
      locationTypes.length
    ) {
      for (let i = 0; i < restrictedTypes.length; i++) {
        const typeId = restrictedTypes[i];
        const type = locationTypes.find(t => t.value === typeId);
        restrictedTypes[i] = type;
      }
    }

    return restrictedTypes;
  };

  const handleSearch = (value) => {
    if (!value || value === '') {
      setText('');
      setIsShowing(true);
      return;
    }
    const target = value.toLowerCase();
    const found = locations.filter(({ name, alias }) => (
      name?.toLowerCase()?.includes(target) || alias?.toLowerCase()?.includes(target)
    ));

    if (found.length === 1) {
      onChange(found[0]);
      setText('');
      return;
    }
    setSearch({ name: value });
    setIsShowing(true);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    setSearch('');
    setText('');
    setIsShowing(false);
  };

  const location = value && locations.length > 0 && locations.find(x => x.id === value);

  return (
    <div className={`location-picker${className ? ` ${className}` : ''}`}>
      {location ? (
        <div className={`location-display${location ? '' : ' empty'}`}>
          {!hidePickerButtons && (
            <div className='picker-buttons'>
              {canClear && value && (
                <button
                  disabled={disabled}
                  type='button'
                  className='button medium white text-align--center margin--none--xxs'
                  onClick={() => onChange({ id: null })}
                >
                  CLEAR
                </button>
              )}
            </div>
          )}
          <React.Fragment>
            <strong>{location.name}</strong>
            <strong>{location.address1}</strong>
            {location.address2 && <strong>{location.address2}</strong>}
            <strong>{location.cityStateZipString}</strong>
          </React.Fragment>
        </div>
      ) : (
        <SearchInput
          value={text}
          onChange={(({ target: { value } }) => setText(value))}
          onSearch={handleSearch}
          keys={['tab']}
          disabled={disabled}
          placeholder={selectLabel}
        />
      )}
      <Modal
        isOpen={isShowing}
        handleClose={handleClose}
        containerStyle='large'
      >
        <div className='modal-header'>
          <h5 className='modal-title' id='add-location-type-modal'>
            Select a Location
          </h5>
          <button
            type='button'
            className='button large close'
            onClick={handleClose}
          />
        </div>
        <section className='modal-body'>
          <MaintenanceTable
            isLoading={isLoading}
            apiError={apiError}
            data={locations.filter(x => !x.deleted)}
            columns={LocationsColumns()} // eslint-disable-line new-cap
            onRowSelect={selected => {
              onChange(selected);
              setSearch('');
              setText('');
              setIsShowing(false);
            }}
            headerChildren={
              <div className="flex" style={{ alignItems: 'flex-end' }}>
                <LocationsToolbar
                  locationTypes={locationTypes}
                  restrictedTypes={getRestrictedTypes()}
                  handleSearch={setSearch}
                  isLoading={isLoading}
                  reset={reset}
                  initialValues={{ name: text }}
                />
                <button
                  type="button"
                  className='button large primary text-align--center toolbar--button--margin'
                  onClick={() => {
                    setReset(true);
                    setSearch('');
                  }}
                >
                  Reset
                </button>
              </div>
            }
          />
        </section>
        <section className='modal-footer toolbar toolbar--xxs'>
          <div className='toolbar--column toolbar--right'>
            <button
              type='button'
              id='location-picker-cancel'
              className='button large white text-align--center margin--none--xxs'
              onClick={handleClose}
            >
              Cancel
            </button>
          </div>
        </section>
      </Modal>
    </div>
  );
};

export default LocationPicker;
